import config from './config';

export type MerchantSocketMessage = { event: string } & Record<string, unknown>;

const MAX_RETRIES = 8;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

export function merchantSocketUrl(path: string, token: string) {
  const trimmed = config.baseUrl.trim().replace(/\/$/, '');
  const httpBase = trimmed || window.location.origin;
  const wsBase = httpBase.replace(/^http/i, 'ws');
  const url = new URL(path, `${wsBase}/`);
  url.searchParams.set('token', token);
  return url.toString();
}

export function readMerchantSocketMessage(value: unknown): MerchantSocketMessage | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const event = (value as { event?: unknown }).event;
  if (typeof event !== 'string' || !event.trim()) return null;
  return value as MerchantSocketMessage;
}

/** 按登录 token 连接一条商家 WebSocket。关闭函数会停止重连。地址不写入日志。 */
export function connectMerchantSocket(options: {
  path: string;
  token: string;
  onEvent: (event: MerchantSocketMessage) => void;
  onReconnect?: () => void;
}) {
  let stopped = false;
  let socket: WebSocket | null = null;
  let attempt = 0;
  let openedOnce = false;
  let timer = 0;

  const connect = () => {
    if (stopped) return;
    let url: string;
    try {
      url = merchantSocketUrl(options.path, options.token);
    } catch {
      return;
    }
    const current = new WebSocket(url);
    socket = current;
    current.onmessage = (message) => {
      if (stopped || current !== socket || typeof message.data !== 'string') return;
      try {
        const parsed = readMerchantSocketMessage(JSON.parse(message.data) as unknown);
        if (parsed) options.onEvent(parsed);
      } catch {
        // 忽略无法解析的帧
      }
    };
    current.onopen = () => {
      if (stopped || current !== socket) {
        current.close();
        return;
      }
      const notify = openedOnce;
      openedOnce = true;
      attempt = 0;
      if (notify) options.onReconnect?.();
    };
    current.onerror = () => {
      if (current === socket) current.close();
    };
    current.onclose = () => {
      if (stopped || current !== socket) return;
      if (attempt >= MAX_RETRIES) return;
      const delay = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);
      attempt += 1;
      timer = window.setTimeout(connect, delay);
    };
  };

  connect();

  return () => {
    stopped = true;
    window.clearTimeout(timer);
    const current = socket;
    socket = null;
    current?.close();
  };
}
